// src/services/googleIntegrationService.js
import axios from 'axios';
import { supabase } from '../backend/supabase';

const BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.lawdie.co'
  : 'http://localhost:4000';

class GoogleIntegrationService {
  constructor() {
    this.axios = axios.create({
      baseURL: BASE_URL,
      withCredentials: true
    });

    // Add auth token to requests if available
    this.axios.interceptors.request.use(async (config) => {
      const authHeader = await this.getAuthHeader();
      if (authHeader) {
        config.headers.Authorization = authHeader;
      }
      return config;
    });
  }

  async getAuthHeader() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (!session?.access_token) return null;
      return `Bearer ${session.access_token}`;
    } catch (error) {
      console.error('Error getting auth header:', error);
      return null;
    }
  }

  async getAuthUrl() {
    try {
      const { data } = await this.axios.get('/api/auth/google/url');
      return data.url;
    } catch (error) {
      console.error('Error getting auth URL:', error);
      throw new Error('Failed to get authentication URL');
    }
  }

  async exchangeCode(code) {
    try {
      const { data } = await this.axios.post('/api/auth/google/exchange-code', { code });
      return data;
    } catch (error) {
      console.error('Error exchanging code:', error);
      throw new Error('Failed to exchange authentication code');
    }
  }

  async checkIntegrationStatus() {
    try {
      const { data } = await this.axios.get('/api/auth/google/status');
      return {
        connected: data.connected,
        profile: data.profile,
        services: data.services
      };
    } catch (error) {
      console.error('Error checking integration status:', error);
      throw new Error('Failed to check integration status');
    }
  }

  async disconnect() {
    try {
      const { data } = await this.axios.post('/api/auth/google/disconnect');
      return data;
    } catch (error) {
      console.error('Error disconnecting:', error);
      throw error;
    }
  }

  // Gmail specific methods
  async sendEmail(to, subject, body) {
    try {
      const { data } = await this.axios.post('/api/email/send', {
        to,
        subject,
        body
      });
      return data;
    } catch (error) {
      console.error('Error sending email:', error);
      throw new Error('Failed to send email');
    }
  }

  // Drive specific methods
  async listFiles(folderId = null) {
    try {
      const { data } = await this.axios.get('/api/drive/files', {
        params: { folderId }
      });
      return data;
    } catch (error) {
      console.error('Error listing files:', error);
      throw new Error('Failed to list files');
    }
  }

  // Calendar specific methods
  async listEvents(timeMin, timeMax) {
    try {
      const { data } = await this.axios.get('/api/calendar/events', {
        params: { timeMin, timeMax }
      });
      return data;
    } catch (error) {
      console.error('Error listing events:', error);
      throw new Error('Failed to list events');
    }
  }
}

// Create and export a single instance
export const googleIntegrationService = new GoogleIntegrationService();